import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';

import { ApiError, apiClient, fleetPath } from '@/lib/api-client';

import { fleetKeys } from './fleet-keys';

/** Body for POST /servers — snake_case to match the API's CreateServerDto. */
export interface CreateServerBody {
  name: string;
  role: string;
  public_ip: string;
  private_ip?: string;
  provider: string;
  region_continent?: string;
  region_country?: string;
  region_city?: string;
  vm_type?: string;
  /** proxy_ingress: the Cloudflare-fronted DNS name it serves HTTPS-CONNECT on. */
  dns_hostname?: string;
  /** VPN/proxy protocols this server runs. Each entry mirrors CreateServerProtocolDto. */
  protocols?: { protocol: string; port?: number }[];
  /** Capability tags ('p2p' | 'tor' | 'mixnet' | 'stealth'…) — drives routing selection. */
  capabilities?: string[];
  ssh_username?: string;
  ssh_password?: string;
  connection_mode?: 'standard' | 'p2p';
  /** When false the API skips the provisioning dispatch — the wizard sends false. */
  auto_provision?: boolean;
}

export interface CreateServerResult {
  /** `created` (201) | `unchanged` (200 idempotent). */
  outcome: string;
  server: { name: string };
}

/** POST /servers — register a fleet server. A 409 surfaces as an ApiError. */
export function useCreateServer(): UseMutationResult<
  CreateServerResult,
  ApiError,
  CreateServerBody
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body) => apiClient.post<CreateServerResult>(fleetPath('/servers'), body),
    onSuccess: () => {
      // Refresh the list, summary cards and filters.
      void queryClient.invalidateQueries({ queryKey: fleetKeys.all });
    },
  });
}
